import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, X } from 'lucide-react';
import { useAddClientForm, type CreatedClientResult } from '../useAddClientForm';
import { BusinessDetailsSection } from './BusinessDetailsSection';
import { ContactPersonSection } from './ContactPersonSection';
import { ClientAddressSection } from './ClientAddressSection';
import { AuthorizationLetterSection } from './AuthorizationLetterSection';
import { CrossAgencyCompanyModal } from './CrossAgencyCompanyModal';

type CreateClientModalProps = {
  open: boolean;
  onClose: () => void;
  onCreated: (result: CreatedClientResult) => void;
};

export function CreateClientModal({
  open,
  onClose,
  onCreated,
}: CreateClientModalProps) {
  const {
    mobileConfig,
    register,
    control,
    setValue,
    watch,
    setError,
    clearErrors,
    errors,
    handleSubmit,
    isSaving,
    authorizationLetterFileName,
    authorizationLetterError,
    isUploadingAuthorizationLetter,
    onSelectAuthorizationLetter,
    clearAuthorizationLetter,
    crossAgencyPrompt,
    onContinueCrossAgency,
    onCancelCrossAgency,
  } = useAddClientForm({
    onCreated: (result) => {
      onCreated(result);
      onClose();
    },
  });

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-end justify-center sm:items-center sm:p-5">
      <button
        type="button"
        className="absolute inset-0 bg-[#0f1b2d]/45 backdrop-blur-[2px]"
        aria-label="Close"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Create client"
        className="camp-fade-up relative z-10 flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-3xl bg-white shadow-[0_24px_64px_-16px_rgba(16,38,73,0.35)] sm:rounded-3xl"
      >
        <div className="shrink-0 px-5 pt-5 pb-3 sm:px-7 sm:pt-6">
          <div className="flex items-start justify-between gap-3 text-left">
            <div className="min-w-0 pr-2">
              <p className="m-0 text-[11px] font-semibold tracking-[0.08em] text-[var(--dash-brand)] uppercase">
                New client
              </p>
              <h2 className="mt-1 mb-0 text-xl font-semibold tracking-tight text-[var(--dash-ink)]">
                Create client
              </h2>
              <p className="mt-1 mb-0 text-sm leading-snug text-[var(--dash-muted)]">
                Add a client and continue this campaign
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#EEF2F6] text-[var(--dash-muted)] transition hover:bg-[#E2E8F0] hover:text-[var(--dash-ink)]"
              aria-label="Close create client"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
        >
          <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-5 py-2 sm:px-7">
            <BusinessDetailsSection
              register={register}
              errors={errors}
              watch={watch}
              setValue={setValue}
              variant="plain"
            />
            <ContactPersonSection
              register={register}
              control={control}
              errors={errors}
              mobileConfig={mobileConfig}
              setError={setError}
              clearErrors={clearErrors}
              variant="plain"
            />
            <ClientAddressSection
              register={register}
              errors={errors}
              variant="plain"
            />
            <AuthorizationLetterSection
              fileName={authorizationLetterFileName}
              isUploading={isUploadingAuthorizationLetter}
              error={authorizationLetterError}
              onSelect={onSelectAuthorizationLetter}
              onClear={clearAuthorizationLetter}
              variant="plain"
            />
          </div>

          <div className="shrink-0 px-5 pt-3 pb-5 sm:px-7 sm:pb-6">
            <div className="flex flex-col-reverse gap-2.5 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                disabled={isSaving}
                className="h-11 rounded-xl px-5 text-sm font-semibold text-[var(--dash-muted)] transition hover:bg-[#EEF2F6] hover:text-[var(--dash-ink)] disabled:opacity-50 sm:min-w-[108px]"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSaving || isUploadingAuthorizationLetter}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-xl px-6 text-sm font-semibold text-white shadow-sm disabled:opacity-50 sm:min-w-[148px]"
                style={{
                  background:
                    'linear-gradient(90deg, #8E58A6 0%, #5A4EA6 25%, #145E8C 50%, #007A78 75%, #33A65B 100%)',
                }}
              >
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Create client
              </button>
            </div>
          </div>
        </form>
      </div>

      <CrossAgencyCompanyModal
        open={crossAgencyPrompt.open}
        companyName={crossAgencyPrompt.companyName}
        isSubmitting={isSaving}
        onCancel={onCancelCrossAgency}
        onContinue={onContinueCrossAgency}
      />
    </div>,
    document.body,
  );
}
